import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Auth.module.css';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { useAuth } from '../../store/AuthContext';
import { useToast } from '../../store/ToastContext';
import { validators, validateForm } from '../../utils/validation';
import { MailIcon } from '../../components/Icons';

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm(formData, {
      email: [validators.required, validators.email],
      password: [validators.required],
    });

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      await login(formData);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Invalid credentials';
      
      // Check if account is not verified - redirect to verification page
      if (errorCode === 'AUTHENTICATION_ERROR' && errorMessage.toLowerCase().includes('not verified')) {
        toast.info('Please verify your email to continue.');
        navigate('/verify-otp', { state: { email: formData.email, purpose: 'registration' } });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h2 className={styles.formTitle}>Welcome back</h2>
        <p className={styles.formSubtitle}>Sign in to your account to continue</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <Input
          label="Email"
          name="email"
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          leftIcon={<MailIcon />}
          autoComplete="email"
        />

        <Input
          label="Password"
          name="password"
          type="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          autoComplete="current-password"
        />

        <div className={styles.formActions}>
          <Link to="/forgot-password" className={styles.link}>
            Forgot password?
          </Link>
        </div>

        <Button type="submit" fullWidth loading={loading}>
          Sign In
        </Button>
      </form>

      <p className={styles.formFooter}>
        Don't have an account?{' '}
        <Link to="/register" className={styles.link}>
          Create account
        </Link>
      </p>
    </div>
  );
}

export default Login;
